import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, exhaustMap, map, of} from "rxjs";
import {PortfolioService} from "../../services/document/portfolio.service";
import {DocumentActions, DocumentTemplateActions} from "../shared.actions";
import {ErrorHandlingUtil} from "../../utils/error-handling-util";
import {ReportService} from "../../services/document/report.service";
import {Store} from "@ngrx/store";
import {selectIdsByTypeForDocumentGeneration} from "./document.selectors";
import {concatLatestFrom} from "@ngrx/operators";
import {TemplateInfo} from "../../model/template-info";
import {MatSnackBar} from "@angular/material/snack-bar";
import {tap} from "rxjs/operators";
import {NotificationActions} from "../../../notification/store/notification.actions";
import {Router} from "@angular/router";

export const loadDocuments = createEffect(
    (actions$ = inject(Actions), portfolioService = inject(PortfolioService)) => {
        return actions$.pipe(
            ofType(DocumentActions.loadDocuments),
            exhaustMap(action =>
                portfolioService.loadDocuments(action.context).pipe(
                    map(documents => DocumentActions.loadDocumentsSuccess({context: action.context, documents})),
                    catchError((errorResponse: any) =>
                        of(DocumentActions.loadDocumentsError({
                            context: action.context,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse) || "Could not load documents"
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const deleteDocument = createEffect(
    (actions$ = inject(Actions), portfolioService = inject(PortfolioService)) => {
        return actions$.pipe(
            ofType(DocumentActions.deleteDocument),
            exhaustMap(action =>
                portfolioService.deleteDocument(action.documentId).pipe(
                    map(() => DocumentActions.deleteDocumentSuccess({context: action.context, documentId: action.documentId})),
                    catchError((errorResponse: any) =>
                        of(DocumentActions.deleteDocumentError({
                            context: action.context,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse) || "Could not delete document"
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);


/* ReportService and DocumentTemplate */
export const loadTemplates = createEffect(
    (actions$ = inject(Actions), reportService = inject(ReportService)) => {
        return actions$.pipe(
            ofType(DocumentTemplateActions.loadTemplates),
            exhaustMap(action =>
                reportService.getTemplates(action.context).pipe(
                    map(documentTemplates => DocumentTemplateActions.loadTemplatesSuccess({documentTemplates})),
                    catchError((errorResponse: any) =>
                        of(DocumentTemplateActions.loadTemplatesError({
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const generateDocument = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), reportService = inject(ReportService), router = inject(Router)) => {
        return actions$.pipe(
            ofType(DocumentTemplateActions.generateDocument),
            concatLatestFrom(() => store$.select(selectIdsByTypeForDocumentGeneration)),
            exhaustMap(([action, idsByType]) => {
                const templateInfo: TemplateInfo = {
                    ...action.templateInfo,
                    idsByType
                };
                // Capture the originating route so the completion notification can
                // deep-link the user back to the page they triggered the report from
                // (the backend validates this same-origin before storing it).
                return reportService.generateDocument(templateInfo, router.url).pipe(
                        map((response) => DocumentTemplateActions.generateDocumentSuccess({taskInfoId: response.taskInfoId})),
                        catchError((errorResponse: any) =>
                            of(DocumentTemplateActions.generateDocumentError({
                                errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)
                            }))
                        )
                    )
                }
            )
        );
    },
    {functional: true}
);

// Switch the notification bell's poll cadence to 5 s while a report job is in flight.
// The matching JOB_FINISHED notification untracks itself when it arrives in the panel list.
export const trackReportJob = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DocumentTemplateActions.generateDocumentSuccess),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        );
    },
    {functional: true}
);

export const showGenerateDocumentSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DocumentTemplateActions.generateDocumentSuccess),
            tap(() => {
                // Report generation now runs async on the backend — we tell the user it's
                // started and the bell will surface the download/deep-link on completion.
                snackBar.open("Report is generating. You'll be notified when it's ready.", "", {
                    duration: 5000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showGenerateDocumentError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DocumentTemplateActions.generateDocumentError),
            tap((action) => {
                snackBar.open(
                    `Failed to generate report.\n${action.errorMsg}`,
                    "Close",
                    {
                        duration: 0,
                        panelClass: ["multiline-snackbar"]
                    }
                );
            })
        );
    },
    {functional: true, dispatch: false}
);
